import { entries, get, isPlainObject, noop, omit, values } from 'lodash-es';
import axios from 'axios';
import lf from 'lovefield';
import { nanoid } from 'nanoid';
import * as Comlink from 'comlink';

import { formatEdmDateTime, formatEdmTime } from '../date';
import { PRIVACY_CONTROLS_MODES } from '../constants';
import { ODATA_DATA_TYPES, getEntityKey } from '../odata/utils';
import { dataflowApiBase, getServiceInstance } from '../service';
import { getPicklistRequestInfo } from 'utils';
import { WORKER_FETCH_STATE, generateDatabaseNamePrefix, initialWorkerState } from './utils';
import { LOG_TYPE } from 'components/ReportViewComponents/constants';

const LOVEFIELD_DATA_TYPES = {
  [ODATA_DATA_TYPES['Edm.Binary']]: lf.Type.STRING,
  [ODATA_DATA_TYPES['Edm.Boolean']]: lf.Type.BOOLEAN,
  [ODATA_DATA_TYPES['Edm.Byte']]: lf.Type.NUMBER,
  [ODATA_DATA_TYPES['Edm.DateTime']]: lf.Type.DATE_TIME,
  [ODATA_DATA_TYPES['Edm.DateTimeOffset']]: lf.Type.DATE_TIME,
  [ODATA_DATA_TYPES['Edm.Decimal']]: lf.Type.NUMBER,
  [ODATA_DATA_TYPES['Edm.Double']]: lf.Type.NUMBER,
  [ODATA_DATA_TYPES['Edm.Guid']]: lf.Type.STRING,
  [ODATA_DATA_TYPES['Edm.Int16']]: lf.Type.INTEGER,
  [ODATA_DATA_TYPES['Edm.Int32']]: lf.Type.INTEGER,
  [ODATA_DATA_TYPES['Edm.Int64']]: lf.Type.INTEGER,
  [ODATA_DATA_TYPES['Edm.SByte']]: lf.Type.NUMBER,
  [ODATA_DATA_TYPES['Edm.Single']]: lf.Type.NUMBER,
  [ODATA_DATA_TYPES['Edm.String']]: lf.Type.STRING,
  [ODATA_DATA_TYPES['Edm.Time']]: lf.Type.DATE_TIME,
};

let dbInstance;
let schemaSnapshot;
let rootPrimaryKeys = [];
let propertiesWithDateTimeDataType = {};
let propertiesWithIntegerOrFloatDataType = [];
let propertiesWithBooleanDataType = {};
let isFetchCancelled = false;

let sharedRequestCancelTokenSourceList = [];

export async function cancelPendingRequests(reason) {
  sharedRequestCancelTokenSourceList.forEach((source) => source?.cancel(reason));
  sharedRequestCancelTokenSourceList = [];
  isFetchCancelled = true;
}

export async function fetchRecordsCount({ connectionId, queryUrl, tenantId, authToken }) {
  const tokenSource = axios.CancelToken.source();
  sharedRequestCancelTokenSourceList.push(tokenSource);

  const $countUrl = createNewUrlInstance(queryUrl);
  $countUrl.searchParams.delete('$expand');
  $countUrl.pathname = $countUrl.pathname.concat('/$count');

  let recordsCount = 0;
  try {
    recordsCount = await getServiceInstance(tenantId, { isWorker: true, authToken }).post(
      `${dataflowApiBase}/connection/${connectionId}/proxy`,
      {
        skipLog: true,
        url: $countUrl,
        type: 'xml',
      },
      {
        cancelToken: tokenSource.token,
      }
    );
  } catch (error) {
    if (axios.isCancel(error)) {
      return error;
    }
  }

  return recordsCount;
}

const buildWorkerState = () => {
  let state = { ...initialWorkerState };

  return {
    actions: {
      reset: () => {
        state = { ...initialWorkerState };
      },
      setFetchState: (fetchState) => {
        state.changeId = nanoid();
        state.fetchState = fetchState;
      },
      setTotalRecords: (totalRecords) => {
        state.changeId = nanoid();
        state.totalRecords = totalRecords;
      },
      setRecordsFetched: (count) => {
        state.changeId = nanoid();
        state.recordsFetched = count;
      },
    },
    state,
  };
};

let workerState = buildWorkerState();

export const getWorkerFetchState = () => workerState.state;

export async function createDBInstance({
  dbName,
  entity,
  expandedNavigationProps,
  expandOptionsMap,
  schema,
  recordsCount,
  connectionId,
  tenantId,
  queryUrl,
  tabId,
  authToken,
  picklistDisplayMode,
  selectedSensitiveMode,
  linkedQuery,
}) {
  // A single-entity run replaces any multi-entity run's tables.
  multiEntityRunId = null;

  try {
    await purgeIDB({ tabId });

    bustCache();

    schemaSnapshot = schema;
    rootPrimaryKeys = entity.property.filter((prop) => prop.isKey);

    const schemaBuilder = lf.schema.create(dbName, 1);

    const entitySelector = `${entity.namespace}.${entity.name}`;
    const entityType = schema.entityTypesMap[entitySelector];

    createTables({
      tableName: entityType.name,
      schemaBuilder,
      entityType,
      expandedNavigationProps,
      expandOptionsMap,
    });

    return schemaBuilder.connect().then(async (db) => {
      dbInstance = db;
      isFetchCancelled = false;

      workerState = buildWorkerState();
      workerState.actions.setTotalRecords(recordsCount);
      workerState.actions.setFetchState(WORKER_FETCH_STATE.FETCHING);

      const cancelTokenSource = axios.CancelToken.source();
      sharedRequestCancelTokenSourceList.push(cancelTokenSource);

      const url = createNewUrlInstance(queryUrl);
      url.searchParams.set('$format', 'json');

      try {
        const response = await getServiceInstance(tenantId, { isWorker: true, authToken }).post(
          `${dataflowApiBase}/connection/${connectionId}/proxy`,
          {
            url: url.toString(),
            type: LOG_TYPE.REPORT_VIEW,
            additional_info: {
              ...getPicklistRequestInfo(picklistDisplayMode),
              should_mask_data: selectedSensitiveMode === PRIVACY_CONTROLS_MODES.MASKING.id,
              should_anonymize_data:
                selectedSensitiveMode === PRIVACY_CONTROLS_MODES.ANONYMIZATION.id,
            },
            linked_query: linkedQuery,
          },
          {
            cancelToken: cancelTokenSource.token,
          }
        );

        if (!response || !response?.d) {
          workerState.actions.setFetchState(WORKER_FETCH_STATE.FETCH_FAILED);
          throw new Error('Failed to fetch data');
        }

        workerState.actions.setFetchState(WORKER_FETCH_STATE.IMPORTING);

        let rawRecords = [];
        if (Array.isArray(response.d)) {
          rawRecords = response.d;
        }

        if (Array.isArray(response.d?.results)) {
          rawRecords = response.d?.results;
        }

        workerState.actions.setRecordsFetched(rawRecords.length);

        if (!rawRecords.length) {
          workerState.actions.setFetchState(WORKER_FETCH_STATE.READY);

          if (isFetchCancelled) {
            return isFetchCancelled;
          }

          return;
        }

        try {
          await importToDatabase(entity, rawRecords);
        } catch (error) {
          workerState.actions.setFetchState(WORKER_FETCH_STATE.IMPORT_FAILED);

          throw new Error(error);
        }
        workerState.actions.setFetchState(WORKER_FETCH_STATE.READY);
      } catch (error) {
        workerState.actions.setFetchState(WORKER_FETCH_STATE.FETCH_FAILED);

        if (error instanceof Error) {
          throw new Error(error.message);
        }

        const { error: _error } = error;

        if (_error) {
          throw new Error(JSON.stringify(_error));
        }

        throw new Error(JSON.stringify(error));
      }
    });
  } catch (error) {
    console.error(`Failed to create database: ${dbName}`, error);
  }
}

/**
 * Writes records into an entity's table and its expand tables
 *
 * @param {Object} entity
 * @param {Array<Object>} rawRecords
 * @param {Object} [db] - Connection every write of this import uses; a newer run may replace
 *   dbInstance while a write is pending
 */
async function importToDatabase(entity, rawRecords, db = dbInstance) {
  let nestedTables = {};
  const records = rawRecords.map((record) => {
    const flattenedRecord = {};
    flattenRecord(omit(record, '__metadata'), flattenedRecord);

    return flattenedRecord;
  });

  const pushDataToTable = async ({
    tableName,
    entity,
    records,
    isNested = false,
    parentTableName = '',
  }) => {
    const _tableName = isNested
      ? getTableName(`nested_${parentTableName}_${tableName}`)
      : tableName;

    const table = db.getSchema().table(_tableName);
    const rows = [];

    if (!isNested) {
      records.forEach((row) => {
        const primaryKeys = rootPrimaryKeys.reduce((acc, key) => {
          return {
            ...acc,
            [key.name]: row[key.name],
          };
        }, {});

        const pk_keyHash = JSON.stringify(primaryKeys);

        const _record = omit(row, '__metadata');

        const formattedBooleanProperties = entries(propertiesWithBooleanDataType).reduce(
          (acc, [colName, prop]) => {
            const originalColumnName = colName.replace(LF_COL_PREFIX, '');
            let boolValue = get(_record, originalColumnName);
            if (boolValue === false) {
              boolValue = 'No';
            } else if (boolValue === true) {
              boolValue = 'Yes';
            }

            return { ...acc, [colName]: boolValue };
          },
          {}
        );

        const formattedDateProperties = entries(propertiesWithDateTimeDataType).reduce(
          (acc, [colName, prop]) => {
            const originalColumnName = colName.replace(LF_COL_PREFIX, '');
            const dateString = get(_record, originalColumnName);
            const formattedDate = getFormattedDateTimeValue(dateString, prop.type);

            return { ...acc, [colName]: formattedDate };
          },
          {}
        );
        const formattedIntegerOrFloatProperties = propertiesWithIntegerOrFloatDataType.reduce(
          (acc, colName) => {
            const originalColumnName = colName.replace(LF_COL_PREFIX, '');
            const value = get(_record, originalColumnName);

            return { ...acc, [colName]: Number(value) };
          },
          {}
        );

        const prefixedRecord = entries(_record).reduce(
          (acc, [key, value]) => ({ ...acc, [getLfColName(key)]: value }),
          {}
        );

        const rowData = {
          ...prefixedRecord,
          ...formattedDateProperties,
          ...formattedIntegerOrFloatProperties,
          ...formattedBooleanProperties,
          _INT_UQ_ID: nanoid(),
          lf_dataflow_keyHash: pk_keyHash,
        };

        rows.push(table.createRow(rowData));

        entries(row).forEach(([propertyName, propertyData]) => {
          if (Array.isArray(propertyData)) {
            const currentNavProp = entity.navigationProperty.find(
              (navProp) => navProp.name === propertyName
            );

            nestedTables = {
              ...nestedTables,
              [propertyName]: {
                tableName: currentNavProp.name,
                parentTableName: _tableName,
                isNested: true,
                data: [
                  ...(nestedTables[propertyName] ? nestedTables[propertyName].data : []),
                  {
                    records: propertyData,
                    lf_dataflow_fk_keyHash: pk_keyHash,
                  },
                ],
              },
            };
          }
        });
      });
    } else {
      records.forEach((row) => {
        row.records.forEach((record) => {
          const _record = omit(record, '__metadata');

          const formattedBooleanProperties = entries(propertiesWithBooleanDataType).reduce(
            (acc, [colName, prop]) => {
              const originalColumnName = colName.replace(LF_COL_PREFIX, '');
              let boolValue = get(_record, originalColumnName);
              if (boolValue === false) {
                boolValue = 'No';
              } else if (boolValue === true) {
                boolValue = 'Yes';
              }

              return { ...acc, [colName]: boolValue };
            },
            {}
          );

          const formattedDateProperties = entries(propertiesWithDateTimeDataType).reduce(
            (acc, [colName, prop]) => {
              const originalColumnName = colName.replace(LF_COL_PREFIX, '');
              const dateString = get(_record, originalColumnName);
              if (dateString) {
                const formattedDate = getFormattedDateTimeValue(dateString, prop.type);
                return { ...acc, [colName]: formattedDate };
              } else {
                return acc;
              }
            },
            {}
          );

          const formattedIntegerOrFloatProperties = propertiesWithIntegerOrFloatDataType.reduce(
            (acc, colName) => {
              const originalColumnName = colName.replace(LF_COL_PREFIX, '');
              const value = get(_record, originalColumnName);

              return { ...acc, [colName]: Number(value) };
            },
            {}
          );

          const prefixedRecord = entries(_record).reduce(
            (acc, [key, value]) => ({ ...acc, [getLfColName(key)]: value }),
            {}
          );

          const rowData = {
            ...prefixedRecord,
            ...formattedDateProperties,
            ...formattedIntegerOrFloatProperties,
            ...formattedBooleanProperties,
            _INT_UQ_ID: nanoid(),
            [`${parentTableName}_lf_dataflow_keyHash`]: row.lf_dataflow_fk_keyHash,
          };

          rows.push(table.createRow(rowData));
        });
      });
    }

    await db.insertOrReplace().into(table).values(rows).exec();
  };

  await pushDataToTable({ tableName: entity.name, entity, records });

  await Promise.all(
    values(nestedTables).map(
      async (nestedTable) =>
        await pushDataToTable({
          tableName: nestedTable.tableName,
          records: nestedTable.data,
          isNested: nestedTable.isNested,
          parentTableName: nestedTable.parentTableName,
        })
    )
  );
}

/**
 * The table a single-entity run reads: its first selected entity, with that entity's expands
 *
 * @param {Object} parsedState - Report view state
 * @returns {{entityName: string, expand: Object}}
 */
const getPrimaryTable = (parsedState) => {
  const [primaryEntity] = parsedState.entity.value;
  return {
    entityName: primaryEntity?.name,
    expand: parsedState.expand.entities?.[getEntityKey(primaryEntity)] ?? {
      value: [],
      optionsMap: {},
    },
  };
};

function createDataQuery(parsedState, table = getPrimaryTable(parsedState)) {
  const selectedEntity = table.entityName;
  const selectedTable = dbInstance.getSchema().table(selectedEntity);
  const { expand } = table;

  const query = dbInstance.select().from(selectedTable);

  // When report view is open, we don't want to do the LEFT OUTER JOIN.
  if (expand.value.length > 0) {
    expand.value.forEach((propertyName) => {
      const propertyData = expand.optionsMap[propertyName];
      if (propertyData.multiplicity === '*') {
        const nestedTable = getNestedTableInstance(propertyData.name, selectedEntity);

        query.leftOuterJoin(
          nestedTable,
          selectedTable.lf_dataflow_keyHash.eq(nestedTable[`${selectedEntity}_lf_dataflow_keyHash`])
        );
      }
    });
  }

  return query;
}

const createCountQuery = (parsedState, table = getPrimaryTable(parsedState)) => {
  const selectedEntity = table.entityName;
  const { expand } = table;
  const selectedTable = dbInstance.getSchema().table(selectedEntity);
  const query = dbInstance.select(selectedTable.lf_dataflow_keyHash).from(selectedTable);

  // Expand is required in createCountQuery incase a filter based on the n-multiplicity property is added
  if (expand.value.length > 0) {
    expand.value.forEach((propertyName) => {
      const propertyData = expand.optionsMap[propertyName];
      if (propertyData.multiplicity === '*') {
        const nestedTable = getNestedTableInstance(propertyData.name, selectedEntity);

        query.leftOuterJoin(
          nestedTable,
          selectedTable.lf_dataflow_keyHash.eq(nestedTable[`${selectedEntity}_lf_dataflow_keyHash`])
        );
      }
    });
  }

  return query;
};

/**
 * Strips LF_COL_PREFIX from object keys. For join results (n-multiplicity),
 * Lovefield returns nested objects per table; we must strip the prefix
 * inside those nested objects too so downstream code sees e.g. "Id" not "lf_col_Id".
 */
const stripLfColPrefixFromRow = (row) => {
  return entries(row).reduce((acc, [key, value]) => {
    const strippedKey = key.startsWith(LF_COL_PREFIX) ? key.slice(LF_COL_PREFIX.length) : key;
    const strippedValue =
      isPlainObject(value) && !Array.isArray(value) ? stripLfColPrefixFromRow(value) : value;
    acc[strippedKey] = strippedValue;
    return acc;
  }, {});
};

/**
 * For join results, nested table rows need _INT_navName set to the nav prop name
 * (e.g. "Orders") so ReportView can group by expand. Table key is "nested_Root_NavProp".
 */
function setNavNameOnNestedRows(queryData, parsedState) {
  const expand = parsedState.expand;
  if (!expand?.value?.length) return;
  const rootTableName = parsedState.entity?.value?.name;
  if (!rootTableName) return;

  queryData.forEach((row) => {
    entries(row).forEach(([tableKey, value]) => {
      if (!tableKey.includes('nested_') || !isPlainObject(value)) return;
      const navName = expand.value.find(
        (name) => getTableName(`nested_${rootTableName}_${name}`) === tableKey
      );
      if (navName) {
        value._INT_navName = navName;
      }
    });
  });
}

/**
 * Reads a page of the current run. For a multi-entity run, `table` picks the entity's table
 * ({ entityName, expand }), and rows come back in search order; limit and skip count rows by
 * that order, so the caller cuts its pages by position.
 */
export async function runPagination(limit, skip, currentState, multiEntityTable = null) {
  const parsedState = JSON.parse(currentState);
  const table = multiEntityTable ?? getPrimaryTable(parsedState);

  const dataQuery = createDataQuery(parsedState, table);
  const countQuery = createCountQuery(parsedState, table);

  if (multiEntityTable) {
    const selectedTable = dbInstance.getSchema().table(table.entityName);
    dataQuery.orderBy(selectedTable[getLfColName(MULTI_ENTITY_COLUMNS.ROW_INDEX)], lf.Order.ASC);
  }

  // Get a transaction object first.
  var txn = dbInstance.createTransaction();

  // exec in order: query 1 first then query2, guaranteed snapshot
  const [rawQueryData, countData] = await txn.exec([dataQuery.limit(limit).skip(skip), countQuery]);
  const queryData = rawQueryData.map(stripLfColPrefixFromRow);

  // Join results: set _INT_navName on nested rows and return expand list for ReportView
  const { expand } = table;
  if (expand?.value?.length > 0) {
    setNavNameOnNestedRows(queryData, {
      ...parsedState,
      expand,
      entity: { value: { name: table.entityName } },
    });
  }

  return {
    queryData,
    totalCount: countData.length,
    expandedPropertyNames: expand?.value ?? [],
  };
}

// Columns a multi-entity table adds to each row: its position in the search, so rows line
// up across linked tables and pages are cut by it, and the person's other records in that
// entity, as JSON.
export const MULTI_ENTITY_COLUMNS = {
  ROW_INDEX: '_INT_row_index',
  OTHER_RECORDS: '_INT_other_records',
};

// Entity key to { entity, primaryKeys, columnTypes } of each table in a multi-entity run.
let multiEntityTables = {};
// Run whose tables the database holds. Setup and imports await, so a newer run can start in
// between; the older one then stops instead of writing into the newer run's tables.
let multiEntityRunId = null;

const snapshotColumnTypes = () => ({
  dateTime: propertiesWithDateTimeDataType,
  numeric: propertiesWithIntegerOrFloatDataType,
  boolean: propertiesWithBooleanDataType,
});

const restoreColumnTypes = ({ dateTime, numeric, boolean }) => {
  propertiesWithDateTimeDataType = dateTime;
  propertiesWithIntegerOrFloatDataType = numeric;
  propertiesWithBooleanDataType = boolean;
};

const resetColumnTypes = () => restoreColumnTypes({ dateTime: {}, numeric: [], boolean: {} });

/**
 * Creates the database of a multi-entity run: one table per entity, linked or not, each with
 * its own expands. Rounds are added to it with importMultiEntityRound.
 *
 * @param {Object} params
 * @param {string} params.runId
 * @param {string} params.dbName
 * @param {Array<{entityKey: string, entity: Object, expand: Object}>} params.entities
 * @param {Object} params.schema
 * @param {string} params.tabId
 * @returns {Promise<boolean>} false when a newer run started before the tables were ready
 */
export async function createMultiEntityDBInstance({ runId, dbName, entities, schema, tabId }) {
  multiEntityRunId = runId;

  await purgeIDB({ tabId });

  if (multiEntityRunId !== runId) {
    return false;
  }

  bustCache();

  schemaSnapshot = schema;
  multiEntityTables = {};

  const schemaBuilder = lf.schema.create(dbName, 1);

  entities.forEach(({ entityKey, entity, expand }) => {
    const entityType = schema.entityTypesMap[`${entity.namespace}.${entity.name}`];

    resetColumnTypes();
    createTables({
      tableName: entityType.name,
      schemaBuilder,
      entityType,
      expandedNavigationProps: expand?.value ?? [],
      expandOptionsMap: expand?.optionsMap ?? {},
      extraColumns: [
        [MULTI_ENTITY_COLUMNS.ROW_INDEX, lf.Type.INTEGER],
        [MULTI_ENTITY_COLUMNS.OTHER_RECORDS, lf.Type.STRING],
      ],
    });

    multiEntityTables[entityKey] = {
      entity,
      // Rows are identified by their position in the search: the query may not select the
      // entity's own key fields, and each position holds one person.
      primaryKeys: [{ name: MULTI_ENTITY_COLUMNS.ROW_INDEX }],
      columnTypes: snapshotColumnTypes(),
    };
  });

  const connection = await schemaBuilder.connect();

  if (multiEntityRunId !== runId) {
    connection.close();
    return false;
  }

  dbInstance = connection;
  isFetchCancelled = false;
  workerState = buildWorkerState();
  workerState.actions.setFetchState(WORKER_FETCH_STATE.READY);
  return true;
}

/**
 * Adds one round of a multi-entity search to the run's tables. A linked row shows its first
 * record and keeps the person's other records for the row's dropdown.
 *
 * @param {Object} round
 * @param {string} round.runId - Run the round belongs to
 * @param {Array<Object>} round.linkedRows - [{ index, key, [entityKey]: [record, ...] }]
 * @param {Object} round.independent - { [entityKey]: { rows: [{ index, ...record }] } }
 * @returns {Promise<boolean>} false when the tables belong to a newer run
 */
export async function importMultiEntityRound({ runId, linkedRows = [], independent = {} }) {
  if (multiEntityRunId !== runId) {
    return false;
  }

  workerState.actions.setFetchState(WORKER_FETCH_STATE.IMPORTING);

  // This run's connection and tables: a newer run replaces both while a write is pending.
  const db = dbInstance;
  const tables = multiEntityTables;
  const isCurrent = () => multiEntityRunId === runId;

  const recordsByEntity = Object.keys(tables).reduce((acc, entityKey) => {
    const linked = linkedRows
      .filter((row) => row[entityKey]?.length)
      .map((row) => {
        const [shown, ...others] = row[entityKey];
        return {
          ...shown,
          [MULTI_ENTITY_COLUMNS.ROW_INDEX]: row.index,
          [MULTI_ENTITY_COLUMNS.OTHER_RECORDS]: others.length ? JSON.stringify(others) : null,
        };
      });
    const unlinked = (independent[entityKey]?.rows ?? []).map(({ index, ...record }) => ({
      ...record,
      [MULTI_ENTITY_COLUMNS.ROW_INDEX]: index,
      [MULTI_ENTITY_COLUMNS.OTHER_RECORDS]: null,
    }));

    return { ...acc, [entityKey]: [...linked, ...unlinked] };
  }, {});

  try {
    // Tables are filled one at a time: importToDatabase reads the shared column-type state.
    // eslint-disable-next-line no-restricted-syntax
    for (const [entityKey, records] of entries(recordsByEntity)) {
      if (!isCurrent()) {
        return false;
      }

      if (records.length) {
        const { entity, primaryKeys, columnTypes } = tables[entityKey];
        restoreColumnTypes(columnTypes);
        rootPrimaryKeys = primaryKeys;
        // eslint-disable-next-line no-await-in-loop
        await importToDatabase(entity, records, db);
      }
    }
  } catch (error) {
    // A replaced run's failed write must not mark the newer run's import as failed.
    if (!isCurrent()) {
      return false;
    }
    workerState.actions.setFetchState(WORKER_FETCH_STATE.IMPORT_FAILED);
    throw new Error(error);
  }

  if (!isCurrent()) {
    return false;
  }

  // The tables keep every round, so the count adds this round's rows to the earlier ones.
  workerState.actions.setRecordsFetched(
    workerState.state.recordsFetched +
      linkedRows.length +
      values(independent).reduce((total, table) => total + (table.rows?.length ?? 0), 0)
  );
  workerState.actions.setFetchState(WORKER_FETCH_STATE.READY);
  return true;
}

// Helper Functions
const bustCache = () => {
  schemaSnapshot = {};
  rootPrimaryKeys = [];
  propertiesWithDateTimeDataType = {};
  propertiesWithIntegerOrFloatDataType = [];
};

function deleteDB(dbName) {
  return new Promise((resolve, reject) => {
    const dbDeleteRequest = indexedDB.deleteDatabase(dbName);
    dbDeleteRequest.onerror = () => {
      reject(`Failed to delete database: ${dbName}`);
    };
    dbDeleteRequest.onsuccess = () => {
      resolve(`Deleted database ${dbName}`);
    };
    dbDeleteRequest.onblocked = () => {
      reject(`Failed to delete database: ${dbName}`);
    };
  });
}

export async function purgeIDB({ tabId }) {
  if (dbInstance) {
    dbInstance.close();
  }
  const databases = await indexedDB.databases();
  await Promise.all(
    databases.map(async (db) => {
      if (db.name.startsWith(generateDatabaseNamePrefix({ tabId }))) {
        await deleteDB(db.name);
      }
    })
  ).catch(noop);
}

const createNewUrlInstance = (_url) => {
  const url = new URL(_url);
  url.searchParams.delete('$format');
  url.searchParams.delete('$inlinecount');
  url.searchParams.delete('paging');

  return url;
};

const getTableName = (nameString) => nameString.replace('.', '_');

const LF_COL_PREFIX = 'lf_col_';
const getLfColName = (name) => `${LF_COL_PREFIX}${name}`;

const createTables = ({
  tableName,
  schemaBuilder,
  entityType,
  isNested = false,
  parentTableName = '',
  expandedNavigationProps = [],
  expandOptionsMap = {},
  extraColumns = [],
}) => {
  const primaryKeys = ['_INT_UQ_ID'];
  const nullableProperties = [];

  const _tableName = isNested ? getTableName(`nested_${parentTableName}_${tableName}`) : tableName;

  const tableBuilder = schemaBuilder
    .createTable(_tableName)
    .addColumn(
      isNested ? `${parentTableName}_lf_dataflow_keyHash` : 'lf_dataflow_keyHash',
      LOVEFIELD_DATA_TYPES['Edm.String']
    )
    .addColumn('_INT_UQ_ID', LOVEFIELD_DATA_TYPES['Edm.Int16']);

  entityType.property.forEach((prop) => {
    const colName = getLfColName(prop.name);
    if (prop.isKey) {
      primaryKeys.push(colName);
    } else if (prop.nullable) {
      nullableProperties.push(colName);
    }

    tableBuilder.addColumn(colName, LOVEFIELD_DATA_TYPES[prop.type]);

    if (
      prop.type === ODATA_DATA_TYPES['Edm.DateTime'] ||
      prop.type === ODATA_DATA_TYPES['Edm.DateTimeOffset'] ||
      prop.type === ODATA_DATA_TYPES['Edm.Time']
    ) {
      propertiesWithDateTimeDataType = {
        ...propertiesWithDateTimeDataType,
        [colName]: prop,
      };
    } else if (
      prop.type === ODATA_DATA_TYPES['Edm.Decimal'] ||
      prop.type === ODATA_DATA_TYPES['Edm.Double'] ||
      prop.type === ODATA_DATA_TYPES['Edm.Int16'] ||
      prop.type === ODATA_DATA_TYPES['Edm.Int32'] ||
      prop.type === ODATA_DATA_TYPES['Edm.Int64']
    ) {
      propertiesWithIntegerOrFloatDataType.push(colName);
    } else if (prop.type === ODATA_DATA_TYPES['Edm.Boolean']) {
      propertiesWithBooleanDataType = {
        ...propertiesWithBooleanDataType,
        [colName]: prop,
      };
    }
  });

  extraColumns.forEach(([name, type]) => {
    tableBuilder.addColumn(getLfColName(name), type);
    nullableProperties.push(getLfColName(name));
  });

  if (!isNested) {
    expandedNavigationProps.forEach((navProp) => {
      const navigationProperty = expandOptionsMap[navProp];

      const association = schemaSnapshot.associationsMap[navigationProperty.relationship];
      const toRole = association.end.find(({ role }) => role === navigationProperty.toRole);

      const expandedEnity = schemaSnapshot.entityTypesMap[toRole.type];

      // Create seperate table for each N-multiplicity property
      if (navigationProperty.multiplicity === '*') {
        createTables({
          tableName: navigationProperty.name,
          schemaBuilder,
          entityType: expandedEnity,
          isNested: true,
          parentTableName: entityType.name,
        });
      }
      // Flatten out the 1-multiplicity property
      else {
        expandedEnity.property.forEach((prop) => {
          const flatColName = `${navigationProperty.name}_${prop.name}`;
          const colName = getLfColName(flatColName);
          if (prop.nullable) {
            nullableProperties.push(colName);
          }

          tableBuilder.addColumn(colName, LOVEFIELD_DATA_TYPES[prop.type]);

          if (
            prop.type === ODATA_DATA_TYPES['Edm.DateTime'] ||
            prop.type === ODATA_DATA_TYPES['Edm.DateTimeOffset'] ||
            prop.type === ODATA_DATA_TYPES['Edm.Time']
          ) {
            propertiesWithDateTimeDataType = {
              ...propertiesWithDateTimeDataType,
              [colName]: prop,
            };
          } else if (
            prop.type === ODATA_DATA_TYPES['Edm.Decimal'] ||
            prop.type === ODATA_DATA_TYPES['Edm.Double'] ||
            prop.type === ODATA_DATA_TYPES['Edm.Int16'] ||
            prop.type === ODATA_DATA_TYPES['Edm.Int32'] ||
            prop.type === ODATA_DATA_TYPES['Edm.Int64']
          ) {
            propertiesWithIntegerOrFloatDataType.push(colName);
          } else if (prop.type === ODATA_DATA_TYPES['Edm.Boolean']) {
            propertiesWithBooleanDataType = {
              ...propertiesWithBooleanDataType,
              [colName]: prop,
            };
          }
        });
      }
    });

    tableBuilder.addUnique('uq_lf_dataflow_keyHash', ['lf_dataflow_keyHash']);
  } else {
    tableBuilder.addForeignKey(`lf_dataflow_fk_keyHash`, {
      local: `${parentTableName}_lf_dataflow_keyHash`,
      ref: `${parentTableName}.lf_dataflow_keyHash`,
    });
  }

  tableBuilder.addPrimaryKey(primaryKeys).addNullable(nullableProperties);
};

function flattenRecord(record, rootObject, keyPrefix = '') {
  entries(record).forEach(([key, value]) => {
    if (isPlainObject(value)) {
      if (value.__metadata) {
        flattenRecord(omit(value, '__metadata'), rootObject, `${key}_`);
      } else if (keyPrefix === '' && value.results && Array.isArray(value.results)) {
        rootObject[`${keyPrefix}${key}`] = value.results;
      }
    } else {
      rootObject[`${keyPrefix}${key}`] = value;
    }
  });
}

const getFormattedDateTimeValue = (dateString, type) => {
  if (
    type === ODATA_DATA_TYPES['Edm.DateTime'] ||
    type === ODATA_DATA_TYPES['Edm.DateTimeOffset']
  ) {
    return dateString ? formatEdmDateTime({ dateTime: dateString, returnObj: true }) : null;
  } else if (type === ODATA_DATA_TYPES['Edm.Time']) {
    return dateString ? formatEdmTime({ time: dateString, returnObj: true }) : null;
  }
};

const getNestedTableInstance = (tableName, rootTableName) => {
  return dbInstance.getSchema().table(getTableName(`nested_${rootTableName}_${tableName}`));
};

const getFilteredQuery = ({ rule, selectedTable, expand, rootTableName }) => {
  let propertyName = rule.property.name;
  let _selectedTable = selectedTable;

  // If the query is against an expanded n-multiplicty navProperty, then we change the selectTable and the propertyName
  if (propertyName.includes('/')) {
    const [nestedTableName, columnName] = propertyName.split('/');
    const propertyData = expand.optionsMap[nestedTableName];
    if (propertyData.multiplicity === '*') {
      _selectedTable = getNestedTableInstance(propertyData.name, rootTableName);
      propertyName = getLfColName(columnName);
    } else {
      propertyName = getLfColName(`${nestedTableName}_${columnName}`);
    }
  } else {
    propertyName = getLfColName(propertyName);
  }

  if (!_selectedTable[propertyName]) {
    return;
  }

  const filter = rule.filter.key;
  const param = rule.param;
  const type = rule.property.type;
  let regx = '';

  switch (filter) {
    case 'isTrue': {
      return _selectedTable[propertyName].eq(true);
    }
    case 'isFalse': {
      return _selectedTable[propertyName].eq(false);
    }
    case 'eq': {
      return _selectedTable[propertyName].eq(param);
    }
    case 'ne': {
      return _selectedTable[propertyName].neq(param);
    }
    case 'gt': {
      return _selectedTable[propertyName].gt(param);
    }
    case 'ge': {
      return _selectedTable[propertyName].gte(param);
    }
    case 'lt': {
      return _selectedTable[propertyName].lt(param);
    }
    case 'le': {
      return _selectedTable[propertyName].lte(param);
    }
    case 'in': {
      const _param = param.split(';').map((value) => {
        if (type === ODATA_DATA_TYPES['Edm.String']) {
          return value;
        }

        return Number(value);
      });

      return _selectedTable[propertyName].in(_param);
    }
    case 'roundEq': {
      const lowerBound = param - 0.5;
      const upperBound = param + 0.5;

      return lf.op.and(
        _selectedTable[propertyName].gte(lowerBound),
        _selectedTable[propertyName].lt(upperBound)
      );
    }
    case 'floorEq': {
      const lowerBound = param;
      const upperBound = param + 1;

      return lf.op.and(
        _selectedTable[propertyName].gte(lowerBound),
        _selectedTable[propertyName].lt(upperBound)
      );
    }
    case 'ceilingEq': {
      const lowerBound = param - 1;
      const upperBound = param;

      return lf.op.and(
        _selectedTable[propertyName].gt(lowerBound),
        _selectedTable[propertyName].lte(upperBound)
      );
    }

    case 'caseInsensitiveEq': {
      regx = new RegExp(`${param}\\b`, 'gi');
      return _selectedTable[propertyName].match(regx);
    }
    case 'caseInsensitiveNe': {
      regx = new RegExp(`[^${param}]`, 'gi');
      return _selectedTable[propertyName].match(regx);
    }
    case 'startsWith': {
      regx = new RegExp(`^${param}`, 'g');
      return _selectedTable[propertyName].match(regx);
    }
    case 'notStartsWith': {
      regx = new RegExp(`^[^${param}]`, 'g');
      return _selectedTable[propertyName].match(regx);
    }
    case 'endsWith': {
      regx = new RegExp(`${param}$`, 'g');
      return _selectedTable[propertyName].match(regx);
    }
    case 'notEndsWith': {
      regx = new RegExp(`[^${param}]$`, 'g');
      return _selectedTable[propertyName].match(regx);
    }
    case 'contains': {
      regx = new RegExp(`${param}`, 'g');
      return _selectedTable[propertyName].match(regx);
    }
    case 'notContains': {
      regx = new RegExp(`^((?!${param}).)*$`, 'g');
      return _selectedTable[propertyName].match(regx);
    }
    case 'datetimeEq': {
      const utcDate = formatEdmDateTime({ dateTime: param, returnObj: true });
      return _selectedTable[propertyName].eq(utcDate);
    }
    case 'datetimeNe': {
      const utcDate = formatEdmDateTime({ dateTime: param, returnObj: true });
      return _selectedTable[propertyName].neq(utcDate);
    }
    case 'datetimeEqOrBefore': {
      const utcDate = formatEdmDateTime({ dateTime: param, returnObj: true });
      return _selectedTable[propertyName].lte(utcDate);
    }
    case 'datetimeBefore': {
      const utcDate = formatEdmDateTime({ dateTime: param, returnObj: true });
      return _selectedTable[propertyName].lt(utcDate);
    }
    case 'datetimeAfter': {
      const utcDate = formatEdmDateTime({ dateTime: param, returnObj: true });
      return _selectedTable[propertyName].gt(utcDate);
    }
    case 'datetimeEqOrAfter': {
      const utcDate = formatEdmDateTime({ dateTime: param, returnObj: true });
      return _selectedTable[propertyName].gte(utcDate);
    }
    case 'timeEq': {
      const utcDate = formatEdmDateTime({ dateTime: param, returnObj: true });
      return _selectedTable[propertyName].eq(utcDate);
    }
    case 'timeNe': {
      const utcDate = formatEdmDateTime({ dateTime: param, returnObj: true });
      return _selectedTable[propertyName].neq(utcDate);
    }
    case 'timeEqOrBefore': {
      const utcDate = formatEdmDateTime({ dateTime: param, returnObj: true });
      return _selectedTable[propertyName].lte(utcDate);
    }
    case 'timeBefore': {
      const utcDate = formatEdmDateTime({ dateTime: param, returnObj: true });
      return _selectedTable[propertyName].lt(utcDate);
    }
    case 'timeAfter': {
      const utcDate = formatEdmDateTime({ dateTime: param, returnObj: true });
      return _selectedTable[propertyName].gt(utcDate);
    }
    case 'timeEqOrAfter': {
      const utcDate = formatEdmDateTime({ dateTime: param, returnObj: true });
      return _selectedTable[propertyName].gte(utcDate);
    }

    default: {
      return;
    }
  }
};

Comlink.expose({
  cancelPendingRequests,
  fetchRecordsCount,
  createDBInstance,
  createMultiEntityDBInstance,
  importMultiEntityRound,
  runPagination,
  getWorkerFetchState,
});
